export type StatementSendPermissions = {
  canViewQueue: boolean;
  canPrepare: boolean;
  canSendLatest: boolean;
  canSendHistorical: boolean;
  canEditSubject: boolean;
  canEditMessage: boolean;
  canSelectRecipients: boolean;
  canUseManualOverride: boolean;
  canUseCc: boolean;
  canUseBcc: boolean;
  canConfirmSend: boolean;
  canCancelSend: boolean;
  canRetry: boolean;
  canGovernResend: boolean;
  canViewHistory: boolean;
  canViewProviderResponse: boolean;
  canViewAudit: boolean;
  canExportReport: boolean;
};

export type StatementSendQueueItem = {
  companyId: string;
  clientId?: string | null;
  customerId: string;
  accountId: string;
  statementId: string;
  statementNumber: string;
  statementDate?: string | null;
  balanceCents: number;
  lifecycleStatus: string;
  customerName: string;
  customerNumber: string;
  clientCompany: string;
  recipientName: string;
  recipientEmail: string;
  documentVersionId?: string | null;
  versionNumber?: number | null;
  documentStatus: string;
  isActive: boolean;
  fileSize?: number | null;
  generatedAt?: string | null;
  deliveryReadiness: string;
  lastSentAt?: string | null;
  sendCount: number;
  deliveryStatus: string;
  failureCategory?: string | null;
  failureReason?: string | null;
};

export type StatementSendRecipient = {
  recipientId: string;
  recipientType: string;
  name: string;
  companyName: string;
  email: string;
  status: string;
  optOut: boolean;
  source: string;
  validationStatus: string;
  validationIssues: string[];
  groupMemberRecipientIds: string[];
  associations: Array<{
    associationType: string;
    associationId: string;
    associationName: string;
    deliveryRole: "TO" | "CC" | "BCC";
    isDefault: boolean;
    isManualOverride: boolean;
    priority: number;
  }>;
};

export type StatementSendVersion = {
  documentVersionId: string;
  versionNumber: number;
  documentStatus: string;
  isActive: boolean;
  contentHash?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  storageProvider?: string | null;
  storageKeyPresent: boolean;
  generatedBy?: string | null;
  generatedAt?: string | null;
  correctionReason?: string | null;
  createdAt: string;
};

export type StatementSendPreparation = {
  preparationId: string;
  statementId: string;
  accountId: string;
  documentVersionId: string;
  versionNumber: number;
  sourceWorkspace: string;
  actorName?: string | null;
  actorRole: string;
  status: string;
  recipients: {
    accepted: Array<{
      recipientId?: string | null;
      name: string;
      email: string;
      role: "TO" | "CC" | "BCC";
      recipientType: string;
      source: string;
      manualOverride: boolean;
    }>;
    blocked: Array<{ recipientId?: string | null; name: string; email: string; role: string; reason: string }>;
    duplicateWarnings: Array<{ recipientId?: string | null; name: string; email: string; role: string; reason: string }>;
    priorSend?: {
      attemptId: string;
      status: string;
      providerMessageId?: string | null;
      requestedAt?: string | null;
      active: boolean;
    } | null;
  };
  subject: string;
  messageBody: string;
  templateId?: string | null;
  templateVersion?: string | null;
  attachmentName: string;
  attachmentSize: number;
  attachmentChecksum: string;
  provider: string;
  senderName: string;
  senderEmail: string;
  replyToName?: string | null;
  replyToEmail?: string | null;
  readiness: { ready: boolean; checks: Array<{ label: string; passed: boolean }> };
  manualOverrideReason?: string | null;
  historicalSendReason?: string | null;
  duplicateSendReason?: string | null;
  retryOfAttemptId?: string | null;
  resendOfAttemptId?: string | null;
  correlationId: string;
  confirmedAt?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type StatementSendAttempt = {
  attemptId: string;
  statementId: string;
  accountId: string;
  documentVersionId?: string | null;
  recipients?: StatementSendPreparation["recipients"] | null;
  subject: string;
  provider?: string | null;
  providerMessageId?: string | null;
  requestedBy?: string | null;
  requestedAt?: string | null;
  providerAcceptedAt?: string | null;
  status: string;
  failureCategory?: string | null;
  failureMessage?: string | null;
  attemptNumber: number;
  retryOfAttemptId?: string | null;
  resendOfAttemptId?: string | null;
  correlationId: string;
  providerResponse?: {
    providerStatus?: string | null;
    responseCode?: string | null;
    safeSummary?: string | null;
    receivedAt?: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
};

export type StatementSendDetail = {
  statement: {
    statementId: string;
    statementNumber: string;
    statementDate?: string | null;
    customerName: string;
    customerNumber: string;
    accountId: string;
    clientCompany?: string | null;
    balanceCents: number;
    lifecycleStatus: string;
    recipientName?: string | null;
    recipientEmail?: string | null;
    placementId?: string | null;
  };
  versions: StatementSendVersion[];
  recipients: StatementSendRecipient[];
  history: StatementSendAttempt[];
  audit: Array<{
    auditId: string;
    sendPreparationId?: string | null;
    deliveryAttemptId?: string | null;
    documentVersionId?: string | null;
    versionNumber?: number | null;
    eventType: string;
    resultStatus: string;
    sourceWorkspace: string;
    actorName?: string | null;
    actorRole: string;
    correlationId: string;
    metadata: Record<string, unknown>;
    errorCode?: string | null;
    safeErrorMessage?: string | null;
    occurredAt: string;
  }>;
  permissions: StatementSendPermissions;
  provider: {
    adapter: string;
    configured: boolean;
    senderName: string;
    senderEmail: string;
    replyToName: string;
    replyToEmail: string;
    missingConfiguration?: string[];
    providerAcceptanceIsNotFinalDelivery: boolean;
  };
  template: {
    templateId: string;
    templateVersion: string;
    subject: string;
    messageBody: string;
  };
  maximumRecipients: number;
};

export type StatementSendPreparationInput = {
  versionId: string;
  sourceWorkspace: string;
  recipientSelections: Array<{ recipientId: string; role: "TO" | "CC" | "BCC" }>;
  manualRecipients: Array<{ email: string; name: string; role: "TO" | "CC" | "BCC"; reason: string }>;
  subject: string;
  messageBody: string;
  templateId: string;
  templateVersion: string;
  historicalSendReason?: string;
  duplicateSendReason?: string;
  retryOfAttemptId?: string;
  resendOfAttemptId?: string;
};

async function fetchJson<T>(url: string, init?: RequestInit) {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers: { Accept: "application/json", ...(init?.headers || {}) },
  });
  if (!response.ok) throw new Error((await response.text()) || "Statement Send request failed.");
  return response.json() as Promise<T>;
}

export async function listStatementSendQueue(options: Record<string, string | number | undefined>) {
  const params = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  return fetchJson<{
    items: StatementSendQueueItem[];
    permissions: StatementSendPermissions;
    pagination: { page: number; pageSize: number; total: number; totalPages: number; hasNext: boolean; hasPrevious: boolean };
  }>(`/api/ar-recovery/statements/sends?${params.toString()}`);
}

export async function getStatementSendDetail(statementId: string) {
  return fetchJson<StatementSendDetail>(`/api/ar-recovery/statements/${encodeURIComponent(statementId)}/sends`);
}

export async function prepareStatementSend(statementId: string, input: StatementSendPreparationInput) {
  const correlationId = crypto.randomUUID();
  return fetchJson<{ preparation: StatementSendPreparation }>(
    `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/sends`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...input,
        correlationId,
        idempotencyKey: `statement-send:${statementId}:${input.versionId}:${correlationId}`,
      }),
    },
  );
}

export async function cancelStatementSend(statementId: string, preparationId: string, reason: string) {
  return fetchJson<{ preparation: StatementSendPreparation }>(
    `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/sends/${encodeURIComponent(preparationId)}/cancel`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    },
  );
}

export async function submitStatementSend(statementId: string, preparationId: string) {
  return fetchJson<{
    attempt: StatementSendAttempt;
    providerResponse?: {
      provider: string;
      providerMessageId?: string | null;
      status: string;
      responseCode: number;
      acceptedRecipients: string[];
      rejectedRecipients: string[];
      safeSummary: string;
      responseTimestamp: string;
      finalInboxDeliveryConfirmed: boolean;
    };
    replay: boolean;
  }>(
    `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/sends/${encodeURIComponent(preparationId)}/submit`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
  );
}

export async function retryStatementSend(statementId: string, attemptId: string, reason: string) {
  return fetchJson<{ preparation: StatementSendPreparation }>(
    `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/sends/attempts/${encodeURIComponent(attemptId)}/retry`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    },
  );
}

export async function getStatementSendReports() {
  return fetchJson<{
    reports: {
      summary: {
        accepted: number;
        partiallyAccepted: number;
        rejected: number;
        retryableFailures: number;
        finalFailures: number;
        retries: number;
        resends: number;
        historicalVersionSends: number;
      };
      records: Array<Record<string, unknown>>;
    };
  }>("/api/ar-recovery/statements/sends?view=reports");
}

export async function downloadStatementSendReport() {
  const response = await fetch("/api/ar-recovery/statements/sends?view=export", { credentials: "include" });
  if (!response.ok) throw new Error((await response.text()) || "Statement Send report export failed.");
  const disposition = response.headers.get("Content-Disposition") || "";
  return {
    blob: await response.blob(),
    fileName: disposition.match(/filename="([^"]+)"/i)?.[1] || "statement-send-report.csv",
  };
}
